/**
 * Session Storage Utility
 * 
 * Handles storing and retrieving session data to ensure persistence across page refreshes
 */

const SESSION_PREFIX = 'planningPoker_session_';
const PARTICIPANT_PREFIX = 'planningPoker_participant_';
const ITEMS_PREFIX = 'planningPoker_items_';

// --- Participant Specific Storage ---

export const storeParticipantForSession = (sessionId, participant) => {
  if (!sessionId || !participant || !participant.id) { // Ensure participant has an ID
    console.warn('storeParticipantForSession called with invalid sessionId or participant data', { sessionId, participant });
    return false;
  }
  try {
    const key = `${PARTICIPANT_PREFIX}${sessionId}`;
    console.log(`Storing participant for session ${sessionId} in localStorage with key ${key}:`, participant);
    localStorage.setItem(key, JSON.stringify(participant));
    return true;
  } catch (error) {
    console.error(`Error storing participant for session ${sessionId} in localStorage:`, error);
    return false;
  }
};

export const getParticipantForSession = (sessionId) => {
  if (!sessionId) {
    console.warn('getParticipantForSession called with invalid sessionId');
    return null;
  }
  try {
    const key = `${PARTICIPANT_PREFIX}${sessionId}`;
    const participantStr = localStorage.getItem(key);
    if (!participantStr) {
      // console.log(`No participant found in localStorage for session ${sessionId} with key ${key}`);
      return null;
    }
    const participant = JSON.parse(participantStr);
    // console.log(`Retrieved participant for session ${sessionId} from localStorage with key ${key}:`, participant);
    return participant;
  } catch (error) {
    console.error(`Error parsing stored participant for session ${sessionId}:`, error);
    return null;
  }
};

export const clearParticipantForSession = (sessionId) => {
  if (!sessionId) {
    console.warn('clearParticipantForSession called with invalid sessionId');
    return;
  }
  const key = `${PARTICIPANT_PREFIX}${sessionId}`;
  console.log(`Clearing participant for session ${sessionId} from localStorage with key ${key}`);
  localStorage.removeItem(key);
};


// --- Session Object Specific Storage ---

export const storeSessionObject = (session) => { // Renamed from storeSession to be more specific
  if (!session || !session.sessionId) {
    console.warn('storeSessionObject called with invalid session data', { session });
    return false;
  }
  try {
    const key = `${SESSION_PREFIX}${session.sessionId}`;
    console.log(`Storing session object for ${session.sessionId} in localStorage with key ${key}:`, session);
    localStorage.setItem(key, JSON.stringify(session));
    return true;
  } catch (error) {
    console.error(`Error storing session object for ${session.sessionId} in localStorage:`, error);
    return false;
  }
};

export const getStoredSessionObject = (sessionId) => { // Renamed from getStoredSession
  if (!sessionId) {
    console.warn('getStoredSessionObject called with invalid sessionId');
    return null;
  }
  try {
    const key = `${SESSION_PREFIX}${sessionId}`;
    const sessionStr = localStorage.getItem(key);
    if (!sessionStr) {
      // console.log(`No session object found in localStorage for session ${sessionId} with key ${key}`);
      return null;
    }
    const session = JSON.parse(sessionStr);
    // console.log(`Retrieved session object for ${sessionId} from localStorage with key ${key}:`, session);
    return session;
  } catch (error) {
    console.error(`Error parsing stored session object for ${sessionId}:`, error);
    // Don't clear here, let the caller decide.
    return null;
  }
};

export const clearStoredSessionObject = (sessionId) => {
  if (!sessionId) {
    console.warn('clearStoredSessionObject called with invalid sessionId');
    return;
  }
  const key = `${SESSION_PREFIX}${sessionId}`;
  console.log(`Clearing session object for ${sessionId} from localStorage with key ${key}`);
  localStorage.removeItem(key);
};

// --- Combined Clearing ---

// Clear all data for a specific session
export const clearAllDataForSession = (sessionId) => {
  if (!sessionId) {
    console.warn('clearAllDataForSession called with invalid sessionId');
    return;
  }
  console.log(`Clearing all data for session ${sessionId} from localStorage`);
  clearStoredSessionObject(sessionId);
  clearParticipantForSession(sessionId);
  clearStoredItems(sessionId); // Assuming clearStoredItems exists and takes sessionId
};

// --- Item Storage (Remains mostly the same but ensure clearStoredItems exists) ---

export const storeItems = (sessionId, items) => {
  if (!sessionId || !items) return false;
  try {
    localStorage.setItem(`${ITEMS_PREFIX}${sessionId}`, JSON.stringify(items));
    return true;
  } catch (error) {
    console.error('Error storing items in localStorage:', error);
    return false;
  }
};

export const getStoredItems = (sessionId) => {
  if (!sessionId) return null;
  try {
    const itemsStr = localStorage.getItem(`${ITEMS_PREFIX}${sessionId}`);
    if (!itemsStr) return null;
    return JSON.parse(itemsStr);
  } catch (error) {
    console.error('Error retrieving stored items:', error);
    return null;
  }
};

export const clearStoredItems = (sessionId) => {
  if (!sessionId) {
    console.warn('clearStoredItems called with invalid sessionId');
    return;
  }
  const key = `${ITEMS_PREFIX}${sessionId}`;
  console.log(`Clearing items for session ${sessionId} from localStorage with key ${key}`);
  localStorage.removeItem(key);
};


// --- Old functions - to be removed or refactored if still used elsewhere ---
// For now, let's comment them out to see where they break.
/*
// Store the session details
export const storeSession = (session, participant) => {
  if (!session) {
    console.warn('storeSession called with null/undefined session');
    return false;
  }
  
  if (!participant) {
    console.warn('storeSession called with null/undefined participant');
    return false;
  }
  
  try {
    console.log('Storing session in localStorage:', session.sessionId);
    localStorage.setItem('planningPoker_session', JSON.stringify(session));
    
    console.log('Storing participant in localStorage:', participant.id || participant._id);
    localStorage.setItem('planningPoker_participant', JSON.stringify(participant));
    
    console.log('Session data successfully stored in localStorage');
    return true;
  } catch (error) {
    console.error('Error storing session in localStorage:', error);
    return false;
  }
};

// Retrieve the stored session
export const getStoredSession = () => {
  try {
    const sessionStr = localStorage.getItem('planningPoker_session');
    const participantStr = localStorage.getItem('planningPoker_participant');
    
    if (!sessionStr) {
      console.warn('No session found in localStorage');
      return null;
    }
    
    if (!participantStr) {
      console.warn('No participant found in localStorage');
      return null;
    }
    
    const session = JSON.parse(sessionStr);
    const participant = JSON.parse(participantStr);
    
    console.log('Retrieved session from localStorage:', session.sessionId);
    console.log('Retrieved participant from localStorage:', participant.id || participant._id);
    
    return { session, participant };
  } catch (error) {
    console.error('Error parsing stored session:', error);
    clearStoredSession(); // This old version cleared everything
    return null;
  }
};

// Clear the stored session
export const clearStoredSession = () => {
  console.log('Clearing all session data from localStorage');
  
  // Clear session and participant data
  localStorage.removeItem('planningPoker_session');
  localStorage.removeItem('planningPoker_participant');
  
  // Clear all items for all sessions
  const itemsKeys = Object.keys(localStorage).filter(
    key => key.startsWith('planningPoker_items_')
  );
  
  console.log(`Found ${itemsKeys.length} item collections to remove from localStorage`);
  itemsKeys.forEach(key => {
    console.log(`Removing ${key} from localStorage`);
    localStorage.removeItem(key);
  });
};
*/

// --- Utility functions that might need to be re-evaluated based on new storage ---

// Check if the stored session matches a specific session ID
// This function might not be directly useful anymore if we don't have a single "global" stored session.
// Instead, components should try to load session data for THEIR specific sessionId.
export const hasMatchingStoredSession = (sessionId) => {
  const storedSessionObject = getStoredSessionObject(sessionId);
  return storedSessionObject && storedSessionObject.sessionId === sessionId;
};

// Check if the participant has a specific role FOR A GIVEN SESSION
export const hasRoleInSession = (sessionId, role) => {
  const storedParticipant = getParticipantForSession(sessionId);
  return storedParticipant && storedParticipant.role === role;
};

// Check specifically if the participant is a facilitator FOR A GIVEN SESSION
export const isFacilitatorInSession = (sessionId) => {
  return hasRoleInSession(sessionId, 'facilitator');
};

// Get path for the correct view based on role FOR A GIVEN SESSION
// This logic becomes more complex. The primary use was for initial routing.
// The Session.js component itself should handle its own rendering or redirection.
export const getCorrectPathForSession = (sessionId) => {
  if (!sessionId) return '/'; // Go home if no session ID
  
  const storedParticipant = getParticipantForSession(sessionId);
  if (storedParticipant && storedParticipant.id) {
    // If we have participant data for this session, assume they can go to the meeting page
    return `/meeting/${sessionId}`;
  }
  // Otherwise, they need to join
  return `/join/${sessionId}`;
};

// It's good practice to also export a function to clear ALL planning poker related storage
// for complete cleanup if needed (e.g., a master reset button in debug, or if storage gets corrupted)
export const clearAllPlanningPokerStorage = () => {
  console.log("Clearing ALL Planning Poker related data from localStorage.");
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('planningPoker_')) {
      localStorage.removeItem(key);
    }
  });
  console.log("All Planning Poker localStorage cleared.");
}; 